import { SelfServiceSchedulePaneProps } from '@tableau/self-service-schedules';
import { DialogType } from '@tableau/widgets';
import * as React from 'react';
import { Provider } from 'react-redux';
import * as _ from 'underscore';
import { incrementRouterSpaceReactKey } from '../../app/actions/spaces/IncrementRouterSpaceReactKeyAction';
import { shareContent } from '../../app/actions/spaces/ShareContentAction';
import { appStore } from '../../app/AppStore';
import { DevicePreviewModalBuilder } from '../../devicePreview/utils/DevicePreviewModalBuilder';
import { PublishCompleteDialog, PublishCompleteDialogProps } from '../../app/content/PublishCompleteDialog';
import { Messages } from '../../codegen/Localize';
import { PublishCompleteDialogDisplayedEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { L10n } from '../../ts/L10n/L10n';
import { ISyncPublishResult } from '../controllers/DatasourcePublishResultCtrl';
import { AngularContext } from '../factories/AngularContextFactory';
import { FeatureFlags } from '../FeatureFlags';
import { formatListAsString } from '../../schedule/utils/ListToStringFormatter';
import { IScheduleFrequencyDescriptionFilter } from '../filters/ScheduleFrequencyDescriptionFilter';
import { Filter } from '../helpers/Filter';
import { Order } from '../helpers/Order';
import { RefreshPropertyHelper } from '../helpers/RefreshPropertyHelper';
import { IDataSourceBase, IDataSourceDetailInfo } from '../models/IDataSourceInfo';
import { IViewInfo } from '../models/IViewInfo';
import { IWorkbookDetailInfo } from '../models/IWorkbookInfo';
import { Module } from '../Module';
import { DataSources, RefreshOptionType } from '../resources/Datasources';
import { ExtractTasks } from '../../extract/resources/ExtractTasks';
import { RemoteRefreshAgents } from '../resources/RemoteRefreshAgents';
import { Views } from '../../views/resources/Views';
import { Workbooks } from '../resources/Workbooks';
import { IGetRemoteRefreshAgentsResult } from '../ServerApi/requests/GetRemoteRefreshAgentsRequest';
import { RemoteRefreshTypes } from '../ServerApi/requests/GetRemoteRefreshSchedulesRequest';
import { FilterFieldNames } from '../ServerApi/types/IFilterClause';
import { RefreshModeTypes } from '../ServerApi/types/IRemoteRefreshProperties';
import { IResult } from '../ServerApi/types/IResponse';
import { DefinedBy, ISchedule } from '../../schedule/types/ISchedule';
import {
  CreateExtractTasksAction,
  CreateExtractTasksMode,
  IExtractTaskSchedule,
} from '../services/CreateExtractTasksAction';
import { ChangeDatasourceRefreshModeAction } from './ChangeDatasourceRefreshModeAction';
import {
  ChangeDatasourceRemoteRefreshPropertiesAction,
  IRemoteRefreshAgentOption,
  IRemoteRefreshPropertiesEditModel,
} from './ChangeDatasourceRemoteRefreshPropertiesAction';
import { ChangeScheduleAction } from './ChangeScheduleAction';
import { ConfirmActionDialog } from './ConfirmActionDialog';
import { getDefaultSelfServiceSchedulePanePropsForSSSWidget } from './CreateRemoteRefreshScheduleAction';
import { PublishDatasourceAction } from './PublishDatasourceAction';
import { ModalInstance, ReactModalService } from './ReactModalService';
import { ToasterService, ToastType } from './ToasterService';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import {
  createSelfServiceScheduleExtractAction,
  setExtractRefreshMode
} from '../../app/sagas/spaces/extractTasks/CreateExtractRefreshSaga';
import {IContentInfo} from '../models/IContentInfo';
import {createExtractRefresh} from '../../app/actions/spaces/CreateExtractRefreshAction';
import { SessionSiteSelectors } from '../../app/selectors/SessionInfoSelectors';
import { RefreshSelfServiceScheduleDialog } from '../../app/content/RefreshSelfServiceScheduleDialog';
import { getRefreshSSSDialogParams } from '../../app/sagas/spaces/extractTasks/CreateExtractRefreshSaga';

export enum PostPublishStep {
  Done,
  Schedule,
  LiveQuerySync
}

interface IDatasourceServerTab {
  datasource: IDataSourceBase;
  allowedMode: CreateExtractTasksMode;
  extractTask?: IExtractTaskSchedule;
  resetSchedule: () => void;
  iconClassFn: () => string;
}

interface IScheduleInfo {
  refreshType: string;
}

interface IDatasourceRemoteRefreshWithSSS {
  datasource: IDataSourceBase;
  dataSourceDetail: IDataSourceDetailInfo;
  selfServiceSchedulePaneProps: SelfServiceSchedulePaneProps;
  availableTypeOptions: string[];
  publishResult: ISyncPublishResult;
  remoteAgentOptions: IRemoteRefreshAgentOption[];
  scheduleSummary?: string;
  scheduleInfo?: IScheduleInfo;
  editModel?: IRemoteRefreshPropertiesEditModel;
  resetSchedule: () => void;
  iconClassFn: () => string;
}

interface ISchedulePublishedWorkbookScopeWithSSS extends ng.IScope {
  workbookTab: {
    workbook: IWorkbookDetailInfo;
    allowedMode: CreateExtractTasksMode;
    resetSchedule: () => void;
    extractTask?: IExtractTaskSchedule;
    iconClassFn: () => string;
  };
  datasourceServerRefreshTabs: IDatasourceServerTab[];
  datasourceRemoteRefreshTabs: IDatasourceRemoteRefreshWithSSS[];
}

export class PublishWorkbookAction {

  public static $inject: any = [
    '$filter',
    '$q',
    '$window',
    '$rootScope',
    'ChangeScheduleAction',
    'CreateExtractTasksAction',
    'ConfirmActionDialog',
    'DataSources',
    'Workbooks',
    'PublishDatasourceAction',
    'ChangeDatasourceRefreshModeAction',
    'RemoteRefreshAgents',
  ];

  constructor(private $filter: ng.IFilterService,
              private $q: ng.IQService,
              private $window: ng.IWindowService,
              private $rootScope: ng.IRootScopeService,
              private ChangeScheduleAction: ChangeScheduleAction,
              private CreateExtractTasksAction: CreateExtractTasksAction,
              private ConfirmActionDialog: ConfirmActionDialog,
              private DataSources: DataSources,
              private Workbooks: Workbooks,
              private PublishDatasourceAction: PublishDatasourceAction,
              private ChangeDatasourceRefreshModeAction: ChangeDatasourceRefreshModeAction,
              private RemoteRefreshAgents: RemoteRefreshAgents) {
  }

  private angularContext: AngularContext;
  public dialogWithPreviewModalInstance: ModalInstance<unknown>;

  public handlePublishedWorkbook(workbook: IWorkbookDetailInfo, publishedDatasourceUrlNames: string[], publishedDatasourcesWithLqsUrlNames: string[], publishResult: ISyncPublishResult, angularContext: AngularContext) {
    this.angularContext = angularContext;

    // Make our best attempt to fire this off, even when the window closes.
    const unloadListener = () =>
      this.RemoteRefreshAgents.completeWorkflowDuringWindowUnload(publishResult.remoteAgentWorkflowId);
    this.$window.addEventListener('unload', unloadListener, false);

    // Don't attempt to look up remote agent count if datasync isn't enabled on this server.
    const remoteAgentsPromise = ServerSettingsUnauthenticated.isBridgeEnabled() ?
      this.RemoteRefreshAgents.get() : this.$q.when({});

    this.$q.all([
      this.getWorkbookExtractTaskCount(workbook),
      this.DataSources.getPublishedByUrlNames(publishedDatasourceUrlNames),
      this.DataSources.getPreferredRefreshOptionsByUrlName(publishedDatasourceUrlNames),
      this.Workbooks.isWorkbookServerRefreshable(workbook.id),
      remoteAgentsPromise
    ]).then((results: any[]): ng.IPromise<any> => {
        const workbookExtractTaskCount = results[0];
        const datasources = results[1];
        const preferredRefreshOptions = results[2];
        const isWorkbookServerRefreshable = results[3] && results[3].refreshable;
        const hasRemoteAgents =  ServerSettingsUnauthenticated.isBridgeEnabled() && !_.isEmpty(results[4].agents);

        const shouldRefreshWorkbook = (workbookExtractTaskCount === 0 && isWorkbookServerRefreshable);
        const serverRefreshDatasources: IDataSourceBase[] = [];
        const remoteRefreshDatasources: IDataSourceBase[] = [];
        const remoteLiveSyncDatasources: IDataSourceBase[] = [];

        _.forEach(datasources.items, (datasource: IDataSourceBase) => {
          const refreshOptions = preferredRefreshOptions[datasource.urlName];
          if (refreshOptions && refreshOptions.suggestion) {
            switch (refreshOptions.suggestion) {
              case RefreshOptionType.Server:
                serverRefreshDatasources.push(datasource);
                break;
              case RefreshOptionType.OnlineSync:
                if (hasRemoteAgents || this.PublishDatasourceAction.canReserveSyncWithWorkflow(publishResult)) {
                  remoteRefreshDatasources.push(datasource);
                }
                break;
              case RefreshOptionType.LiveConnection:
                if(publishedDatasourcesWithLqsUrlNames.indexOf(datasource.urlName) !== -1 && hasRemoteAgents) {
                  remoteLiveSyncDatasources.push(datasource);
                }
                break;
            }
          }
        });

        // There are six different cases here:
        // Case 1: There's nothing to schedule, so show a "Done" button that closes the dialog. (This includes "remote refreshes required, but no agents are available." and "The workbook already has a schedule.")
        // Case 2: Only the workbook needs a schedule, so show the workbook extract refresh schedule dialog.
        // Case 3: Only a single datasource needs a schedule, and it's a server refresh, so show the datasource extract server-refresh schedule dialog.
        // Case 4: Only a single datasource needs a schedule, and it's a remote refresh, so show the datasource extract remote-refresh schedule dialog.
        // Case 5: There are just live query sync datasources, so show the corresponding text
        // Case 6: More than one thing needs a schedule or an agent, so show the tabbed schedule interface with those things.
        if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
          // Case 1: There's nothing to schedule, so show a "Done" button that closes the dialog.
          // (This includes "remote refreshes required, but no agents are available." and "The workbook already has a schedule.")

          return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.Done);

        } else if (shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
          // Case 2: Only the workbook needs a schedule, so show the workbook extract refresh schedule dialog.

          return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
            .then(() => this.CreateExtractTasksAction.forWorkbook(workbook, {
              scope: this.$rootScope,
              targetElement: null
            }, CreateExtractTasksMode.AllowScheduleOnServer))
            .then(() => appStore.dispatch(incrementRouterSpaceReactKey()));

        } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 1 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length === 0) {
          // Case 3: Only a single datasource needs a schedule, and it's a server refresh, so show the datasource extract server-refresh schedule dialog.

          const datasource = serverRefreshDatasources[0];
          return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
            .then(() => this.changeDSRefreshMode(datasource, RefreshModeTypes.Server))
            .then(() => this.CreateExtractTasksAction.forDatasource(datasource, {
              scope: this.$rootScope,
              targetElement: null
            }, CreateExtractTasksMode.AllowScheduleOnServer))
            .then(() => appStore.dispatch(incrementRouterSpaceReactKey()));

        } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 1 && remoteLiveSyncDatasources.length === 0) {
          // Case 4: Only a single datasource needs a schedule, and it's a remote refresh, so show the datasource extract remote-refresh schedule dialog.

          const datasource = remoteRefreshDatasources[0];

          if (FeatureFlags.bridgeServerTriggeredClientSideRefresh) {
            if (FeatureFlags.selfServiceSchedulesForRefreshEnabled && SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(appStore.getState())) {
              if (datasource.isFileBased) {
                return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
                  .then(() => this.ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Remote, null, true, false))
                  .then(() => this.CreateExtractTasksAction.forDatasource(datasource, {
                    scope: this.$rootScope,
                    targetElement: null
                  },  CreateExtractTasksMode.AllowScheduleOnlyOnClient)
                    .then(() => appStore.dispatch(incrementRouterSpaceReactKey())));
              } else{
                const contentInfo = [datasource] as IContentInfo[];
                const action = createExtractRefresh(contentInfo, CreateExtractTasksMode.AllowScheduleOnServer);
                return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
                  .then(() => this.ChangeDatasourceRefreshModeAction.execute(datasource, RefreshModeTypes.Server, null, true, false))
                  .then(() => createSelfServiceScheduleExtractAction(action))
                  .then(() => appStore.dispatch(incrementRouterSpaceReactKey()));
              }
            } else {
              return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
                .then(() => this.changeDSRefreshMode(datasource, RefreshModeTypes.Server))
                .then(() => this.CreateExtractTasksAction.forDatasource(datasource, {
                  scope: this.$rootScope,
                  targetElement: null
                }, CreateExtractTasksMode.AllowScheduleOnServerAndClient))
                .then(() => appStore.dispatch(incrementRouterSpaceReactKey()));
            }
          } else {
          return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
            .then(() => this.displayRemoteRefreshScheduleDialog(publishResult, datasource.id));
          }

        } else if (!shouldRefreshWorkbook && serverRefreshDatasources.length === 0 && remoteRefreshDatasources.length === 0 && remoteLiveSyncDatasources.length >= 1) {
          // Case 5: There are just live query sync datasources so show the corresponding messages

          return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.LiveQuerySync);

        } else {
          // Case 6: More than one thing needs a schedule, so show the tabbed schedule interface with those things.

          return this.RemoteRefreshAgents.get().then((remoteAgents: IGetRemoteRefreshAgentsResult) => {
            const remoteAgentOptions = this.buildRemoteAgentOptions(publishResult, remoteAgents);
            return this.displaySuccessDialog(workbook, datasources.items, PostPublishStep.Schedule)
              .then(() => {
                const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
                const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(appStore.getState());

                // We do not want to show admin schedules option if SSS is enabled
                // A common schedules dialog will be shown for both server and remote refreshes in this multi datasource case
                if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite) {
                  const allDataSources = serverRefreshDatasources.concat(remoteRefreshDatasources);
                  let angularPromise = this.$q.defer();
                  RefreshSelfServiceScheduleDialog.open(getRefreshSSSDialogParams(allDataSources)).then(result => angularPromise.resolve(result))
                    .catch(error => angularPromise.reject(error));
                  return angularPromise.promise;
                }

                return this.displayTabbedScheduleDialogWithSSS(serverRefreshDatasources, remoteRefreshDatasources, publishResult, remoteAgentOptions, shouldRefreshWorkbook, workbook);
              });
          });
        }
      })
      .finally(() => {
        this.$window.removeEventListener('unload', unloadListener, false);
        this.RemoteRefreshAgents.completeWorkflow(publishResult.remoteAgentWorkflowId);
      });
  }

  private displayRemoteRefreshScheduleDialog(publishResult: ISyncPublishResult, datasourceId: string): ng.IPromise<any> {
    // Get more detailed data source first
    return this.DataSources.getById(datasourceId).then((dataSourceDetail: IDataSourceDetailInfo) => {
      return this.PublishDatasourceAction.displayRemoteRefreshScheduleDialog(publishResult, dataSourceDetail);
    });
  }

  private buildRemoteAgentOptions(publishResult: ISyncPublishResult, remoteAgents: IGetRemoteRefreshAgentsResult): IRemoteRefreshAgentOption[] {
    if (this.PublishDatasourceAction.canReserveSyncWithWorkflow(publishResult) && _.isEmpty(remoteAgents)) {
      // Add "My Computer" option if there are no agents
      return [this.PublishDatasourceAction.getNewAgentReservationOption()];
    } else {
      // Use existing remote agents
      return ChangeDatasourceRemoteRefreshPropertiesAction.buildRemoteAgentsList(remoteAgents);
    }
  }

  private displaySuccessDialog(workbook: IWorkbookDetailInfo, datasources: IDataSourceBase[], postPublishStep: PostPublishStep): ng.IPromise<any> {
    const orderClause = [ Order.asc('index') ];
    const viewsQuery = Views.forWorkbook(workbook).withOrder(orderClause);

    return viewsQuery.getCount()
    .then((viewCount) => viewsQuery.getSlice(0, viewCount))
    .then((result) => result.items)
    .then((viewList: IViewInfo[]) =>{
      const dialogProps = this.getPublishCompleteDialogProps(viewList, workbook, datasources, postPublishStep);
      Telemetry.sendEvent<PublishCompleteDialogDisplayedEvent>({
        type: TelemetryEvents.PublishCompleteDialogDisplayed,
        isPreviewDeviceLayoutsLinkDisplayed: dialogProps.shouldDisplayDevicePreview
      });

      return this.buildPublishSuccessDialogFromProps(dialogProps).result;
    });
  }

  public buildPublishSuccessDialogFromProps(props: PublishCompleteDialogProps): ModalInstance<unknown> {
    this.dialogWithPreviewModalInstance = ReactModalService.open({
      type: DialogType.NormalWithTitleBar,
      title: PublishCompleteDialog.Title(),
      dialogContent: React.createElement(PublishCompleteDialog.Content, props),
      footerContent: React.createElement(Provider, {store: appStore},
        React.createElement(PublishCompleteDialog.ConnectedFooter, props)
      ),
      testId: 'publish-workbook-success-modal',
      dialogWidth: 550,
      showCloseButton: true
    });

    return this.dialogWithPreviewModalInstance;
  }

  public getPublishCompleteDialogProps(
    viewList: IViewInfo[],
    workbook: IWorkbookDetailInfo,
    datasources: IDataSourceBase[],
    postPublishStep: PostPublishStep): PublishCompleteDialogProps
  {
    let publishLiveQueryInfo = '';
    if (postPublishStep === PostPublishStep.LiveQuerySync) {
      publishLiveQueryInfo = this.getLiveQueryInfoText(datasources);
    }

    return {
      // We need to use a member variable here because the caller of this method will mutate this modal instance
      // after the dialog is displayed. We need this mutation to propagate to these functions, so we store the
      // instance where we can retrieve it later.
      dismiss: () => this.dialogWithPreviewModalInstance && this.dialogWithPreviewModalInstance.dismiss(),
      close: () => this.dialogWithPreviewModalInstance && this.dialogWithPreviewModalInstance.close(0),
      onDevicePreviewClick: () => this.openDevicePreviewForViz(viewList[0].path, this.dialogWithPreviewModalInstance),
      shareContent: shareContent,
      workbook: workbook,
      datasources: datasources,
      getDatasourceIconClass: PublishWorkbookAction.getDatasourceIconClass,
      publishLiveQueryInfo: publishLiveQueryInfo,
      shouldDisplayDevicePreview: viewList.length > 0,
      shouldDisplayLiveQueryInfo: postPublishStep === PostPublishStep.LiveQuerySync,
      shouldDisplayScheduleButton: postPublishStep === PostPublishStep.Schedule,
    };
  }

  private openDevicePreviewForViz(vizPath: string, publishCompleteModalInstance: ModalInstance<unknown>) {
    Telemetry.sendEvent({
      type: TelemetryEvents.PreviewDeviceLayoutsLinkClicked
    });

    DevicePreviewModalBuilder.openPreview(this.angularContext, vizPath);
    publishCompleteModalInstance.dismiss();
  }

  private getLiveQueryInfoText(datasources: IDataSourceBase[]) {
    const datasourceNames = datasources.map(function(datasource){return datasource.name;}).join(',');
    return Messages.publishWorkbookSuccessLiveQueryInfo({'ITEM_NAME': L10n.elideIfLong(datasourceNames, 50)});
  }

  private displayTabbedScheduleDialogWithSSS(serverRefreshDatasources: IDataSourceBase[], remoteRefreshDatasources: IDataSourceBase[], publishResult: ISyncPublishResult, agents: IRemoteRefreshAgentOption[], shouldRefreshWorkbook: boolean, workbook?: IWorkbookDetailInfo) {
    const dialogScope = <ISchedulePublishedWorkbookScopeWithSSS>this.$rootScope.$new();

    // Build a tab for the workbook, if there is one
    if (workbook && shouldRefreshWorkbook) {
      dialogScope.workbookTab = {
        workbook: workbook,
        allowedMode: CreateExtractTasksMode.AllowScheduleOnServer,
        resetSchedule: () => { dialogScope.workbookTab.extractTask.schedule = null; },
        iconClassFn: () => 'tb-data-pane-workbook-icon'
      };
    }

    // Build a tab for every server-refresh datasource, if there are any
    dialogScope.datasourceServerRefreshTabs = [];
    if (serverRefreshDatasources) {
      _.forEach(serverRefreshDatasources, (datasource: IDataSourceBase) => {
          const datasourceTab: IDatasourceServerTab = {
            datasource: datasource,
            allowedMode: CreateExtractTasksMode.AllowScheduleOnServer,
            resetSchedule: () => { datasourceTab.extractTask.schedule = null; },
            iconClassFn: () => PublishWorkbookAction.getDatasourceIconClass(datasource)
          };
          dialogScope.datasourceServerRefreshTabs.push(datasourceTab);
      });
    }

    // Build a tab for every remote-refresh datasource, if there are any
    dialogScope.datasourceRemoteRefreshTabs = [];
    if (remoteRefreshDatasources) {
      if (FeatureFlags.bridgeServerTriggeredClientSideRefresh) {
        _.forEach(remoteRefreshDatasources, (datasource: IDataSourceBase) => {
          const datasourceTab: IDatasourceServerTab = {
            datasource: datasource,
            allowedMode: CreateExtractTasksMode.AllowScheduleOnServer,
            resetSchedule: () => { datasourceTab.extractTask.schedule = null; },
            iconClassFn: () => PublishWorkbookAction.getDatasourceIconClass(datasource)
          };
          dialogScope.datasourceServerRefreshTabs.push(datasourceTab);
        });
      } else {
        _.forEach(remoteRefreshDatasources, (datasource: IDataSourceBase) => {
          this.DataSources.getById(datasource.id).then((dataSourceDetail: IDataSourceDetailInfo) => {
            const refreshTypes = RefreshPropertyHelper.getAvailableRefreshTypes(dataSourceDetail);
            const datasourceTab: IDatasourceRemoteRefreshWithSSS = {
              datasource: datasource,
              dataSourceDetail: dataSourceDetail,
              selfServiceSchedulePaneProps: getDefaultSelfServiceSchedulePanePropsForSSSWidget(this.ChangeScheduleAction, null),
              availableTypeOptions: refreshTypes,
              scheduleInfo: {
                refreshType: RemoteRefreshTypes.Full,
              },
              publishResult: publishResult,
              remoteAgentOptions: agents,
              resetSchedule: () => {
                datasourceTab.selfServiceSchedulePaneProps = getDefaultSelfServiceSchedulePanePropsForSSSWidget(this.ChangeScheduleAction, null);
                datasourceTab.scheduleInfo = {
                  refreshType: RemoteRefreshTypes.Full,
                };
              },
              iconClassFn: () => PublishWorkbookAction.getDatasourceIconClass(datasource)
            };

            dialogScope.datasourceRemoteRefreshTabs.push(datasourceTab);
          });
        });

        // Remote schedules don't have nice human-readable names, so convert the ISchedule on the fly
        dialogScope.$watch((scope: ISchedulePublishedWorkbookScopeWithSSS) => {
          _.forEach(scope.datasourceRemoteRefreshTabs, (tab) => {
              // id, name, scheduledAction, priority, taskCount, parallel and serverTimezoneOffset are required in ISchedule
              // but not used for IScheduleFrequencyDescriptionFilter
              const schedule: ISchedule = {
                id: '',
                name: '',
                scheduledAction: '',
                priority: 0,
                taskCount: 0,
                parallel: false,
                serverTimezoneOffset: 0,
                definedBy: DefinedBy.SELF_SERVICE,
                scheduleType: tab.selfServiceSchedulePaneProps.recurrenceModel.recurrenceType,
                scheduleDetails: tab.selfServiceSchedulePaneProps.recurrenceModel.recurrenceDetails
              };
              tab.scheduleSummary = this.$filter<IScheduleFrequencyDescriptionFilter>('scheduleFrequencyDescription')(schedule);
          });
        });
      }
    }

    const dialogInstance = this.ConfirmActionDialog.open({
      scope: dialogScope,
      position: {reserveHeight: 550},
      bodyTemplateUrl: 'publishWorkbookTabsWithSSS.html',
      title: Messages.publishWorkbookScheduleDialogTitle(),
      size: '730px',
      actionName: Messages.publishWorkbookScheduleDialogButton_schedule(),
      action: () => {
        const promises: ng.IPromise<IResult>[] = [];
        const itemNames: string[] = [];

        // Create workbook extract task
        if (dialogScope.workbookTab && dialogScope.workbookTab.extractTask && dialogScope.workbookTab.extractTask.schedule) {
          promises.push(this.Workbooks.createExtractTask([workbook.id], dialogScope.workbookTab.extractTask.schedule.id, dialogScope.workbookTab.extractTask.type));
          itemNames.push(workbook.name);
        }

        // Create datasource server refresh extract tasks
        _.forEach(dialogScope.datasourceServerRefreshTabs, (datasourceTab: IDatasourceServerTab) => {
          if (datasourceTab.extractTask && datasourceTab.extractTask.schedule) {
            promises.push(this.DataSources.createExtractTask([datasourceTab.datasource.id], datasourceTab.extractTask.schedule.id, datasourceTab.extractTask.type));
            itemNames.push(datasourceTab.datasource.name);
          }
        });

        // Create datasource remote refresh extract tasks
        _.forEach(dialogScope.datasourceRemoteRefreshTabs, (datasourceTab: IDatasourceRemoteRefreshWithSSS) => {
          promises.push(this.PublishDatasourceAction.createRemoteRefreshSelfServiceSchedule(datasourceTab.datasource, datasourceTab.scheduleInfo.refreshType,
            datasourceTab.selfServiceSchedulePaneProps.recurrenceModel, datasourceTab.editModel, publishResult, /* suppressToast */ true));
          itemNames.push(datasourceTab.datasource.name);
        });

        this.$q.all(promises).then(() => {
          if (itemNames.length > 0) {
            const itemNamesStr = formatListAsString(itemNames);
            ToasterService.instance.pop({ type: ToastType.Success, text: Messages.publishSchedulesReady({ ITEM_NAMES: itemNamesStr, ITEM_COUNT: itemNames.length })});
          }
        });

      }
    });

    dialogInstance.result.finally(() => dialogScope.$destroy());

    return dialogInstance.result;
  }

  private getWorkbookExtractTaskCount(workbook: IWorkbookDetailInfo): ng.IPromise<number> {
    let query = ExtractTasks.all();
    query = query.addFilter(Filter.fieldEq(FilterFieldNames.workbookId, workbook.id));
    return query.getCount();
  }

  private changeDSRefreshMode(datasource: IDataSourceBase, refreshMode: string) {
    // Changing refresh modes causes an error on on-prem servers, so only do it if datasync is an option.
    if (ServerSettingsUnauthenticated.isBridgeEnabled()) {
      return this.ChangeDatasourceRefreshModeAction.execute(datasource, refreshMode, null, true, false);
    } else {
      return this.$q.when({});
    }
  }

  private static getDatasourceIconClass(datasource: IDataSourceBase): string {
    if (!datasource.hasExtracts && !datasource.isHierarchical) {
      return 'tb-data-pane-datasource-icon';
    } else if (datasource.hasExtracts && !datasource.isHierarchical) {
      return 'tb-data-pane-datasource-extract-icon';
    } else {
      return 'tb-data-pane-datasource-cube-icon';
    }
  }
}

Module.instance().service('PublishWorkbookAction', PublishWorkbookAction);
